import { ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  Bars3Icon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  BanknotesIcon as BanknotesIconSolid,
  ClipboardDocumentCheckIcon as ClipboardDocumentCheckIconSolid,
} from '@heroicons/react/24/solid';
import { useTelegram } from '../context/TelegramContext';

const mobileNavItems = [
  { path: '/', label: 'Home', Icon: HomeIcon, IconActive: HomeIconSolid },
  { path: '/pos', label: 'POs', Icon: DocumentTextIcon, IconActive: DocumentTextIconSolid },
  { path: '/permits', label: 'Permits', Icon: ShieldCheckIcon, IconActive: ShieldCheckIconSolid },
  { path: '/payments', label: 'Pay', Icon: BanknotesIcon, IconActive: BanknotesIconSolid },
  { path: '/tasks', label: 'Tasks', Icon: ClipboardDocumentCheckIcon, IconActive: ClipboardDocumentCheckIconSolid },
];

interface MobileLayoutProps {
  children?: ReactNode;
}

export function MobileLayout({ children }: MobileLayoutProps) {
  const { isInTelegram, user, colorScheme } = useTelegram();
  const location = useLocation();

  // Apply Telegram theme styles
  const bgClass = colorScheme === 'dark' ? 'bg-gray-900' : 'bg-gray-50';
  const navBgClass = colorScheme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textClass = colorScheme === 'dark' ? 'text-white' : 'text-gray-900';

  return (
    <div className={`min-h-screen ${bgClass} ${textClass}`}>
      {/* Header */}
      <header className={`${navBgClass} border-b px-4 py-3 flex items-center justify-between sticky top-0 z-40`}>
        <div className="flex items-center gap-3">
          <img
            src="/enana-logo.svg"
            alt="Enana"
            className="w-8 h-8 object-contain"
          />
          <div>
            <h1 className="font-semibold text-sm">Enana PO System</h1>
            {isInTelegram && user && (
              <p className="text-xs text-gray-500">Hi, {user.first_name}</p>
            )}
          </div>
        </div>
        <NavLink to="/settings" className="p-2 rounded-lg hover:bg-gray-100">
          <Bars3Icon className="w-5 h-5" />
        </NavLink>
      </header>

      {/* Main Content */}
      <main className="pb-20 px-4 py-4">
        {children || <Outlet />}
      </main>

      {/* Bottom Navigation */}
      <nav className={`${navBgClass} border-t fixed bottom-0 left-0 right-0 z-50`}>
        <div className="flex justify-around items-center py-2">
          {mobileNavItems.map(({ path, label, Icon, IconActive }) => {
            const isActive = location.pathname === path ||
              (path !== '/' && location.pathname.startsWith(path));
            const IconComponent = isActive ? IconActive : Icon;

            return (
              <NavLink
                key={path}
                to={path}
                className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                  isActive
                    ? 'text-primary'
                    : colorScheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                <IconComponent className="w-5 h-5" />
                <span className="text-xs font-medium">{label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default MobileLayout;
